// Section 22 and 24.14: every number is rendered through <Num>. Given a Target it attaches the
// explain() tooltip (factors grouped by source); given precomputed factors it shows those.
import { useContext } from 'preact/hooks';
import type { Factor, Target } from '../../sim/api';
import { factorTooltip } from '../../viewModels';
import { formatNum, type NumFormat } from '../format';
import { uiText } from '../text/en';
import { ExplainContext } from './explain-context';
import { FactorList } from './factor-list';
import { Tooltip } from './tooltip';

interface Props {
  value: number;
  format?: NumFormat;
  /** Explain this number with explain(game, target) when the tooltip opens. */
  target?: Target;
  /** Or explain it with a factor list already in hand (fragility, flows, death factors). */
  factors?: readonly Factor[];
  /** What the number is, for screen readers ("Food stock"). */
  label?: string;
  class?: string;
  above?: boolean;
}

export function Num({ value, format = 'one', target, factors, label, class: cls, above }: Props) {
  const explain = useContext(ExplainContext);
  const text = formatNum(value, format);
  const numClass = `num${cls === undefined ? '' : ` ${cls}`}`;
  if (target === undefined && factors === undefined) return <span class={numClass}>{text}</span>;
  const content = () => {
    const vm = target !== undefined ? explain(target) : factorTooltip(value, factors ?? []);
    return vm === null ? <p class="factors__none">{uiText.tooltip.none}</p> : <FactorList vm={vm} format={format} />;
  };
  return (
    <Tooltip
      content={content}
      label={label === undefined ? text : `${uiText.tooltip.explain(label)}: ${text}`}
      class={`${numClass} num--explained`}
      {...(above === undefined ? {} : { above })}
    >
      {text}
    </Tooltip>
  );
}
